/** How a rapid-fire round ends: an acknowledgement that waits for Continue — ADR-0004 §3. */
import React from 'react';
import { Zap } from 'lucide-react';
import Button from '../../components/Button';

interface RapidFireOutroProps {
  label: string;
  reason: 'expired' | 'cap';
  answered: number;
  notReached: number;
  onContinue: () => void;
}

const RapidFireOutro: React.FC<RapidFireOutroProps> = ({
  label,
  reason,
  answered,
  notReached,
  onContinue,
}) => {
  const expired = reason === 'expired';
  return (
    <div className="text-center px-8 py-12" data-testid="rapid-fire-outro">
      <div
        className={`w-[72px] h-[72px] rounded-full flex items-center justify-center mx-auto mb-6 ${
          expired ? 'bg-red-50' : 'bg-green-50'
        }`}
      >
        <Zap
          className={`w-8 h-8 ${expired ? 'text-red-600' : 'text-green-600'}`}
          aria-hidden="true"
        />
      </div>

      <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">{label}</p>
      <h1 className="text-3xl font-bold text-gray-900 mb-4">
        {expired ? 'Time' : 'Round complete'}
      </h1>

      <p className="text-5xl font-bold text-gray-900 tabular-nums leading-none">{answered}</p>
      <p className="text-xs font-medium uppercase tracking-widest text-gray-500 mt-2 mb-6">
        {answered === 1 ? 'question answered' : 'questions answered'}
      </p>

      <p className="text-base leading-relaxed text-gray-700 max-w-[44ch] mx-auto mb-8">
        {/* Both reasons consult `notReached`. A cap of 3 on a ten-question block ends the round
            with seven never shown, and "That's all of them" is the one line a respondent would
            use to decide whether they had missed something. */}
        {expired
          ? notReached > 0
            ? `The ${notReached} you didn’t reach are fine — nobody finishes them all. We’re moving on.`
            : 'You got through the lot. We’re moving on.'
          : notReached > 0
            ? `That’s all we needed. The other ${notReached} we’ll leave — we’re moving on.`
            : 'That’s all of them. Nice pace.'}
      </p>

      <Button variant="primary" onClick={onContinue}>
        Continue the survey
      </Button>
    </div>
  );
};

export default RapidFireOutro;
